import { createRootRouteWithContext, HeadContent, Link, Outlet, Scripts, useRouterState } from '@tanstack/react-router'
import { QueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { useEffect, type ReactNode } from 'react'
import { usePageTracking } from '@/hooks/usePageTracking'
import Navbar from '@/components/navbar'
import Footer from '@/components/footer'
import { Button } from '@/components/ui/button'
import { MapPinOffIcon } from 'lucide-react'
import { NewsletterPopup } from '@/features/newsletter/NewsletterPopup'
import { Toaster } from 'sonner'
import { defaultHeadMeta } from '@/lib/seo/siteMeta'
import { initializeSentry } from '@/lib/sentry'
import { initializeGTM, initializeMixpanel, initializeClarity, captureUtmParams } from '@/lib/track'
import globalsCss from '@/globals.css?url'
import fontsCss from '@/fonts.css?url'
import animationsCss from '@/animations.css?url'
import skeletonCss from 'react-loading-skeleton/dist/skeleton.css?url'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  head: () => ({
    meta: [
      { charSet: 'UTF-8' },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1.0, viewport-fit=cover',
      },
      { name: 'theme-color', content: '#F21838' },
      { name: 'apple-mobile-web-app-title', content: 'Mátevolno' },
      // Seznam Webmaster site ownership verification.
      { name: 'seznam-wmt', content: 'R60dF919i1xylwIVB5RqJvVFSE7H18Xe' },
      ...defaultHeadMeta(),
    ],
    links: [
      { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
      { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/icons/apple-touch-icon.png',
      },
      { rel: 'manifest', href: '/manifest.webmanifest' },
      // Warm up the connections the app needs on almost every page.
      {
        rel: 'preconnect',
        href: 'https://api.matevolno.cz',
        crossOrigin: 'anonymous',
      },
      { rel: 'dns-prefetch', href: 'https://api.mapbox.com' },
      { rel: 'stylesheet', href: fontsCss },
      { rel: 'stylesheet', href: globalsCss },
      { rel: 'stylesheet', href: animationsCss },
      { rel: 'stylesheet', href: skeletonCss },
    ],
  }),
})

function NotFoundPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar isLight={false} />
      <main className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 rounded-full bg-slate-100 p-6">
            <MapPinOffIcon className="h-12 w-12 text-slate-400" />
          </div>
          <h1 className="mb-2 text-2xl font-semibold text-slate-700">Stránka nenalezena</h1>
          <p className="mb-6 max-w-sm text-slate-500">
            Omlouváme se, ale stránka kterou hledáte neexistuje nebo byla přesunuta.
          </p>
          <Button asChild>
            <Link to="/">Zpět na hlavní stránku</Link>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  )
}

function RootComponent() {
  usePageTracking()

  // Third-party SDKs touch window/document, so they boot after hydration —
  // never during server rendering.
  useEffect(() => {
    initializeSentry()
    initializeGTM()
    initializeMixpanel()
    initializeClarity()
    captureUtmParams()
  }, [])

  // The /card/* routes are clean render surfaces for generated share images —
  // global overlays must never cover them.
  const isShareCard = useRouterState({
    select: (s) => s.matches.some((match) => match.routeId.startsWith('/card/')),
  })

  return (
    <RootDocument>
      <Outlet />
      {!isShareCard && <NewsletterPopup />}
      <Toaster position="top-center" toastOptions={{ className: 'font-default' }} />
      <ReactQueryDevtools initialIsOpen={false} position="right" />
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </RootDocument>
  )
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="cs">
      <head>
        <HeadContent />
      </head>
      <body className="font-default text-base">
        {children}
        <Scripts />
      </body>
    </html>
  )
}
